import { Card, CardContent } from "@/components/ui/card";
import {
    Carousel,
    CarouselContent,
    CarouselDots,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import type { Route } from "next";
import Image from "next/image";
import Link from "next/link";

export function TestimonialsSection() {
    return (
        <Carousel className="my-8 w-full overflow-hidden p-4 md:my-16 xl:overflow-visible">
            <CarouselContent>
                <TestimonialCard
                    image="https://zoeovucvjce1ksti.public.blob.vercel-storage.com/landing-page/beasters-Fr91ML07Tl4FGp4dEhU0OWEXCZBZRl.jpeg"
                    link="https://www.youtube.com/channel/UC42kVplsGReOedETpGL-C8g"
                    quote="Talentir's Real-Time Payout feature has revolutionized the game, giving me instant access to my hard-earned revenue."
                    creatorName="Beasters"
                    creatorRole="YouTuber"
                    colorPalette="purple"
                />
                <TestimonialCard
                    image="https://zoeovucvjce1ksti.public.blob.vercel-storage.com/landing-page/davinci-abo46YlYDymcxhVy8upCBxOZ4SshCZ.jpg"
                    link="https://www.youtube.com/channel/UCP0g6ygQkYjmog801YnNqtQ"
                    quote="Nothing is smoother than receiving your AdSense directly in your wallet."
                    creatorName="Davinci Jeremie"
                    creatorRole="Crypto Influencer"
                    colorPalette="green"
                />
                <TestimonialCard
                    image="https://zoeovucvjce1ksti.public.blob.vercel-storage.com/landing-page/enkime-KyJ19hb2YLBF5faRwq0uGdHSA8x6wm.jpg"
                    link="https://enkime.de/"
                    quote="Talentir enables us to scale our operations to new heights. It is a pleasure to shape the future with you."
                    creatorName="Vincent"
                    creatorRole="CFO of ENKIME"
                    colorPalette="mint"
                />
                <TestimonialCard
                    image="https://zoeovucvjce1ksti.public.blob.vercel-storage.com/landing-page/rusanda-WBeAYVToAiwd77vGFVeySiFcyll2Jo.jpg"
                    link="https://www.youtube.com/@RusandaPanfili"
                    quote="I love the fact that I can reward my most loyal supporters and let them participate in my success."
                    creatorName="Rusanda Panfili"
                    creatorRole="Violinist of Hans Zimmer"
                    colorPalette="purple"
                />
            </CarouselContent>
            <CarouselDots className="mt-4" />
            <CarouselPrevious />
            <CarouselNext />
        </Carousel>
    );
}

function TestimonialCard({
    image,
    link,
    quote,
    creatorName,
    creatorRole,
    colorPalette,
}: {
    image: string;
    link: string;
    quote: string;
    creatorName: string;
    creatorRole: string;
    colorPalette: "purple" | "green" | "mint";
}) {
    return (
        <CarouselItem className="basis-full md:basis-3/5 lg:basis-2/5">
            <div className="p-1">
                <Card
                    className={cn("relative bg-linear-to-br from-70% from-transparent", {
                        "to-[#F200FF22]": colorPalette === "purple",
                        "to-[#FFF60A22]": colorPalette === "green",
                        "to-[#00FFF022]": colorPalette === "mint",
                    })}
                >
                    {/* <div className="absolute top-2 left-3 font-bold text-6xl leading-none opacity-5">
                        {splitCreators.map((c) => (
                            <p key={c}>{c}</p>
                        ))}
                    </div> */}
                    <Link href={link as Route} target="_blank">
                        <CardContent className={cn("flex h-[320px] items-center justify-center p-6")}>
                            <div className="flex w-full justify-start gap-6">
                                <div className="flex shrink-0 grow-0 basis-[100px] items-center md:basis-[160px]">
                                    <Image
                                        src={image}
                                        width={160}
                                        height={160}
                                        alt="avatar"
                                        className="size-25 rounded-full object-cover md:size-40"
                                    />
                                </div>
                                <div className="flex grow flex-col justify-center">
                                    <Quote colorPalette={colorPalette} />
                                    <blockquote className="mt-4">{quote}</blockquote>
                                    <span className="mt-4 font-semibold text-lg uppercase">{creatorName}</span>
                                    <span className="font-bold text-muted-foreground text-xs">{creatorRole}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Link>
                </Card>
            </div>
        </CarouselItem>
    );
}

function Quote({
    colorPalette,
}: {
    colorPalette: "purple" | "green" | "mint";
}) {
    const fill = () => {
        return `url(#gradient_${colorPalette})`;
    };

    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="43" height="32" viewBox="0 0 43 32" fill="none">
            <title>Quote</title>
            <path
                d="M0 32V22.4924C0 19.7928 0.533478 17.0344 1.60043 14.2173C2.66739 11.4003 4.07518 8.74461 5.8238 6.25034C7.57242 3.75607 9.46923 1.67262 11.5142 0L19.8721 4.88583C18.2123 7.46814 16.849 10.1678 15.7821 12.9849C14.7447 15.8019 14.2261 18.9418 14.2261 22.4044V32H0ZM22.4505 32V22.4924C22.4505 19.7928 22.984 17.0344 24.051 14.2173C25.1179 11.4003 26.5257 8.74461 28.2743 6.25034C30.023 3.75607 31.9198 1.67262 33.9648 0L42.3226 4.88583C40.6629 7.46814 39.2995 10.1678 38.2326 12.9849C37.1953 15.8019 36.6766 18.9418 36.6766 22.4044V32H22.4505Z"
                fill={fill()}
            />
            <defs>
                <linearGradient
                    id={"gradient_purple"}
                    x1="8.75756"
                    y1="-28.9425"
                    x2="46.7438"
                    y2="48.8264"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#00FFF0" />
                    <stop offset="1" stopColor="#F200FF" />
                </linearGradient>
                <linearGradient
                    id={"gradient_green"}
                    x1="8.75756"
                    y1="-28.9425"
                    x2="46.7438"
                    y2="48.8264"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#00FFF0" />
                    <stop offset="1" stopColor="#FFF60A" />
                </linearGradient>
                <linearGradient
                    id={"gradient_mint"}
                    x1="8.75756"
                    y1="-28.9425"
                    x2="46.7438"
                    y2="48.8264"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#28B6A6" />
                    <stop offset="1" stopColor="#00FFF0" />
                </linearGradient>
            </defs>
        </svg>
    );
}
